/**
 * ตัวยิง request ตัวเดียวของทั้งแอป
 *
 * หน้าที่: แนบ token · แปลง snake_case → camelCase · แปลง error ของ DRF เป็นข้อความไทย
 *
 * ⚠️ ห้าม log ตัว response, token หรือ payload — ระบบหลังบ้านเห็นข้อมูลสมาชิกทุกคน
 *    การเผลอ log จึงร้ายแรงกว่าฝั่งแอปลูกค้า (บังคับด้วย eslint rule no-console)
 */

import { camelize, snakeize } from './case';

/**
 * ตัวยิง request ของโมดูล
 *
 * ⚠️ เดิมไฟล์นี้อ่านค่าจาก `@/config/env` (NEXT_PUBLIC_*) — ตัดออกแล้ว
 *    เพราะตอน embed ใน host ค่าเหล่านั้นไม่มีอยู่จริง
 *    host เป็นคนบอก apiBaseUrl / division / token ผ่าน configure() แทน
 *    ทำให้ไฟล์เดียวกันใช้ได้ทั้งสองโหมดโดยไม่มี if
 */
interface HttpConfig {
  apiBaseUrl: string;
  division: string;
}

let httpConfig: HttpConfig = { apiBaseUrl: '', division: '' };

export function configureHttp(config: HttpConfig): void {
  httpConfig = config;
}

export class ApiError extends Error {
  readonly status: number;
  readonly fieldErrors: Record<string, string[]>;

  constructor(status: number, message: string, fieldErrors: Record<string, string[]> = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

/** ผู้ถือ token — ตั้งค่าจาก session store ไม่ให้ http.ts รู้จักที่เก็บ */
let tokenGetter: () => string | null = () => null;
export function setTokenGetter(fn: () => string | null) {
  tokenGetter = fn;
}

/** เรียกเมื่อ backend ตอบ 401 — ให้ชั้น auth ตัดสินใจว่าจะทำอะไรต่อ */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

function messageFromBody(status: number, body: unknown): { message: string; fields: Record<string, string[]> } {
  const fields: Record<string, string[]> = {};
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (typeof b.detail === 'string') return { message: b.detail, fields };
    for (const [k, v] of Object.entries(b)) {
      if (Array.isArray(v)) fields[k] = v.map(String);
    }
    const first = Object.values(fields)[0]?.[0];
    if (first) return { message: first, fields };
  }
  if (status === 401) return { message: 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่', fields };
  if (status === 403) return { message: 'บัญชีนี้ไม่มีสิทธิ์ทำรายการนี้', fields };
  if (status === 404) return { message: 'ไม่พบข้อมูลที่ต้องการ', fields };
  if (status >= 500) return { message: 'ระบบปลายทางขัดข้อง กรุณาลองใหม่อีกครั้ง', fields };
  return { message: 'ทำรายการไม่สำเร็จ กรุณาลองใหม่อีกครั้ง', fields };
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  /** ไม่แนบ token — ใช้กับ endpoint login เท่านั้น */
  anonymous?: boolean;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, anonymous = false } = options;

  if (!httpConfig.apiBaseUrl) {
    /**
     * ล้มพร้อมบอกวิธีแก้ ดีกว่าปล่อยให้ `new URL('')` โยน "Invalid URL"
     * ซึ่งไม่บอกอะไรเลยว่าต้นเหตุคือลืมตั้งค่า (เจอจริงตอน deploy ครั้งแรก)
     */
    throw new ApiError(
      0,
      'ยังไม่ได้ตั้งค่าที่อยู่ของ backend — ต้องเรียก configureHttp() ก่อนยิง request ครั้งแรก ' +
        '(โหมด standalone ตั้งที่ standalone/main.tsx · โหมด embed ให้ host ฉีด CrmApiPort เข้ามาแทน)',
    );
  }

  const url = new URL(httpConfig.apiBaseUrl + path);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': 'th-TH',
    /**
     * ⚠️ CRM module อ่าน division จาก header นี้เท่านั้น
     *    ไม่ส่ง = request.division เป็น None = ทุก endpoint ตอบ 403
     *    endpoint กลุ่ม auth เดิมไม่ได้ใช้ค่านี้ แต่ส่งไปด้วยก็ไม่มีผลเสีย
     *
     * backend ต้องอนุญาต `Division` ใน Access-Control-Allow-Headers
     * ไม่งั้น preflight ไม่ผ่านและ request ไม่ออกจากเบราว์เซอร์เลย
     */
    Division: httpConfig.division,
  };
  /**
   * ⚠️ FormData ต้อง **ไม่ตั้ง Content-Type เอง** — เบราว์เซอร์ต้องเติม boundary ให้
   *    ถ้าตั้งเป็น multipart/form-data เปล่า ๆ backend จะแยกไฟล์ออกจาก body ไม่ได้
   */
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (!anonymous) {
    const token = tokenGetter();
    if (token) headers.Authorization = `Token ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(url.toString(), {
      method,
      headers,
      body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(snakeize(body)),
    });
  } catch {
    /**
     * fetch โยน TypeError โดยไม่บอกสาเหตุ — เบราว์เซอร์ปิดรายละเอียดไว้โดยตั้งใจ
     * สาเหตุที่เป็นไปได้มีสามอย่าง และแยกกันด้วย navigator.onLine ได้บางส่วน
     *
     * ⚠️ กรณีที่พบบ่อยที่สุดตอนพัฒนาคือ **CORS** ซึ่งหน้าตาเหมือนเน็ตหลุดทุกประการ
     *    ถ้าไม่เขียนไว้ตรงนี้ คนที่เจอจะไปไล่หาปัญหาที่เน็ตของตัวเองแทน
     *    ตรวจด้วย `npm run check:cors`
     *
     * ไม่ log ตัว error — URL อาจมีข้อมูลติดมา
     */
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    throw new ApiError(
      0,
      offline
        ? 'เครื่องนี้ไม่ได้เชื่อมต่ออินเทอร์เน็ต กรุณาเชื่อมต่อแล้วลองใหม่'
        : 'เชื่อมต่อกับระบบปลายทางไม่ได้ — อาจเป็นเพราะโดเมนนี้ยังไม่ได้รับอนุญาตจากเซิร์ฟเวอร์ (CORS) หรือเซิร์ฟเวอร์ไม่ตอบสนอง กรุณาแจ้งทีมพัฒนา',
    );
  }

  if (res.status === 204) return undefined as T;

  let parsed: unknown = null;
  const text = await res.text();
  if (text) {
    try { parsed = JSON.parse(text); } catch { parsed = null; }
  }

  if (!res.ok) {
    if (res.status === 401 && !anonymous) onUnauthorized?.();
    const { message, fields } = messageFromBody(res.status, parsed);
    throw new ApiError(res.status, message, fields);
  }

  return camelize<T>(parsed);
}
